import React from 'react';
import { Editor } from 'components';
import { ProductsContext } from 'contexts';
import Container from "./Container";
import Page from './Page';
import {FeaturesSidebar} from "./FeaturesSidebar";

const styles = {
  container: {
    display: 'flex'
  },
  table: {
    width: '100%',
    border: '1px solid',
    borderCollapse: 'collapse',
    margin: '20px 0',
  },
  th: {
    textAlign: 'left',
    padding: '2px 8px',
    fontSize: '13px',
  },
  tdName: {
    width: '30%',
    border: '1px solid',
    textAlign: 'center',
    fontWeight: 'bold',
    fontSize: '13px',
  },
  tdDescription: {
    border: '1px solid',
    fontSize: '13px',
    padding: '4px',
  },
};

const Template = (props) => {
  const {
    removableArea: RemovableArea,
    removeFeaturesFromTable,
    section1,
    changeContent,
    product,
    content,
    typesAndFeatures,
    tblBodyRef,
    index,
  } = props;

  const onContentOverflow = (height) => props.onContentOverflow(index, height);

  const onContentLack = (height) => props.onContentLack(index, height);

  const onRemoveFeature = (e) => {
    removeFeaturesFromTable({
      name: e.target.parentNode.children[0].textContent,
      description: e.target.parentNode.children[1].textContent
    });
  };

  return (
    <Page
      removableArea={RemovableArea}
      onContentOverflow={onContentOverflow}
      onContentLack={onContentLack}
      product={product}
    >
      {section1 && (
        <Editor
          content={content?.section1}
          setContent={(value) => changeContent('section1', value)}
        />
      )}
      {typesAndFeatures.length ? (
        <RemovableArea data-removable={true} id="typesAndFeatures">
          <table style={styles.table}>
            <thead>
              <tr>
                <th colSpan="2" style={styles.th}>
                  TYPES AND FEATURES
                </th>
              </tr>
            </thead>
            <tbody ref={tblBodyRef}>
              {typesAndFeatures.map(({ name, description }) => (
                <tr key={name} onClick={onRemoveFeature}>
                  <td style={styles.tdName}>{name}</td>
                  <td style={styles.tdDescription}>{description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </RemovableArea>
      ) : null}
    </Page>
  );
};

const Layout = (Template) => {
  return class extends React.Component {
    constructor(props) {
      super(props);
      if (Array.isArray(props.content?.pages)) {
        this.state = {
          pages: props.content?.pages.map(p => ({
            typesAndFeatures: [], ...p, tblBodyRef: React.createRef()
          })),
          typesAndFeatures: []
        };
      } else {
        this.state = {
          pages: [
            {
              section1: true,
              typesAndFeatures: [],
              tblBodyRef: React.createRef(),
            },
          ],
          typesAndFeatures: []
        };
      }
    }

    changeContent(pages) {
      const cleanPages = pages.map(p => {
        const { tblBodyRef, ...rest } = p;
        return rest;
      });
      this.props.changeContent('pages', cleanPages);
    }

    componentDidMount() {
      const { product, productsActions } = this.props;
      productsActions.getSimilarProductsForId(product._id).then((p) => {
        this.changeContent(this.state.pages);
        this.setState({ pages: this.state.pages, typesAndFeatures: p });
      });
    }

    onContentOverflow = (index, height) => {
      const tblBodyRef = this.state.pages[index]?.tblBodyRef;
      const typesAndFeatures = this.state.pages[index]?.typesAndFeatures;
      if (tblBodyRef.current) {
        let tblRows = tblBodyRef.current.children;
        let numberRowsToSlice = 0;
        let slicedHeight = 0;

        for (
          let i = tblRows.length - 1;
          i >= 0 && slicedHeight <= height;
          --i
        ) {
          slicedHeight += tblRows[i].scrollHeight;
          ++numberRowsToSlice;
        }

        if (numberRowsToSlice > 0) {
          let nextPage = this.state.pages[index + 1] || {
            typesAndFeatures: [],
            tblBodyRef: React.createRef(),
          };
          nextPage.typesAndFeatures.unshift(
            ...typesAndFeatures.splice(-1 * numberRowsToSlice)
          );

          if (this.state.pages.length - 1 <= index) {
            this.changeContent([...this.state.pages, nextPage]);
            this.setState({
              pages: [...this.state.pages, nextPage],
            });
          } else {
            this.changeContent([...this.state.pages]);
            this.setState({
              pages: [...this.state.pages],
            });
          }
        }
      }
    };

    onContentLack = (index, height) => {
      const tableRowHeight = 24;
      const tableHeaderHeight = 43; // Table margin top included.

      if (height > tableRowHeight) {
        const typesAndFeatures = this.state.pages[index]?.typesAndFeatures;
        if (
          typesAndFeatures.length ||
          height > tableRowHeight + tableHeaderHeight
        ) {
          let nextPage = this.state.pages[index + 1];
          if (nextPage && nextPage.typesAndFeatures.length) {
            let slicedHeight = 0;
            const tblBodyRef = nextPage.tblBodyRef;
            let tblRows = tblBodyRef.current.children;
            for (
              let i = 0;
              i < tblRows.length &&
              slicedHeight + tblRows[i].scrollHeight <= height;
              ++i
            ) {
              typesAndFeatures.push(...nextPage.typesAndFeatures.splice(0, 1));
              slicedHeight += tblRows[i].scrollHeight;
            }

            if (slicedHeight) {
              let newPages = [...this.state.pages].filter(
                (e) => e.section1 || e.typesAndFeatures.length
              );
              this.changeContent(newPages);
              this.setState({
                pages: newPages,
              });
            }
          }
        }
      }
    };

    onAddFeaturesHandler = (featuresList) => {
      const lastPage = this.state.pages[this.state.pages.length - 1];
      lastPage.typesAndFeatures = [...lastPage.typesAndFeatures, ...featuresList];
      this.changeContent([ ...this.state.pages ]);
      this.setState({
        pages: [ ...this.state.pages ],
      });
    };

    removeFeaturesFromTable = ({name, description}) => {
      this.state.pages.forEach(({typesAndFeatures}) => {
        const featureIndex = typesAndFeatures.findIndex(f => f.name === name);
        if (~featureIndex)
          typesAndFeatures.splice(featureIndex, 1);
      });
      this.changeContent([ ...this.state.pages ]);
      this.setState({
        pages: [ ...this.state.pages ],
      });
    };

    get availableTypesAndFeatures() {
      return this.state.typesAndFeatures
        .filter(f =>
          !this.state.pages
            .map(p => p.typesAndFeatures)
            .flat()
            .find(p => p.name === f.name))
        .map(({name, description}) => ({name, description}));
    }

    render() {
      return (
        <div style={styles.container}>
          <Container containerRef={this.props.containerRef}>
            {this.state.pages.map((props, i) => (
              <Template
                {...props}
                changeContent={this.props.changeContent}
                product={this.props.product}
                content={this.props.content}
                onContentOverflow={this.onContentOverflow}
                onContentLack={this.onContentLack}
                removableArea={this.props.removableArea}
                removeFeaturesFromTable={this.removeFeaturesFromTable}
                index={i}
                key={i}
              />
            ))}
          </Container>
          <FeaturesSidebar
            addFeaturesToTable={this.onAddFeaturesHandler}
            typesAndFeatures={this.availableTypesAndFeatures}
          />
        </div>
      );
    }
  };
};

const LT = Layout(Template);

export default (props) => (
  <ProductsContext.Consumer>
    {(value) => <LT {...props} productsActions={value.actions} />}
  </ProductsContext.Consumer>
);
